import { useEffect, useState, useRef, useCallback } from 'react';
import { authManager } from '@/services/authManager';

export interface CacheStats {
  overall: { total: number; cached: number; empty: number };
  leagues: Array<{ id: string; name: string; total: number; cached: number; empty: number }>;
}

export interface CatalogDiscoveryProgress {
  leagueId: string;
  phase: string;
  percent: number;
  message: string;
  detail?: string;
  divisionsFound?: number;
}

interface UseGlobalCatalogStreamOptions {
  onSyncStart?: (globalScheduleId: string, jobId: string) => void;
  onSyncProgress?: (globalScheduleId: string, message: string) => void;
  onSyncComplete?: (globalScheduleId: string, result: any) => void;
  onSyncError?: (globalScheduleId: string, error: string) => void;
  onDiscoveryDone?: (leagueId: string) => void;
  onDiscoveryError?: (leagueId: string, error: string) => void;
}

/**
 * Unified SSE hook for the catalog page.
 * Single EventSource that handles sync_progress, discovery_progress, discovery_done,
 * discovery_error, and cache_stats — replacing separate hooks + polling.
 */
export const useGlobalCatalogStream = (options: UseGlobalCatalogStreamOptions = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [syncingSchedules, setSyncingSchedules] = useState<Set<string>>(new Set());
  const [discoveryProgress, setDiscoveryProgress] = useState<Map<string, CatalogDiscoveryProgress>>(new Map());
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  // Store callbacks in refs to avoid recreating connect() on every render
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const connect = useCallback(async () => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Ensure we have a fresh token before connecting
    await authManager.getAccessToken();
    const token = authManager.getCurrentToken();
    const streamUrl = token
      ? `/api/v1/super-admin/catalog/stream?token=${encodeURIComponent(token)}`
      : `/api/v1/super-admin/catalog/stream`;

    const es = new EventSource(streamUrl);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'connected':
            break;

          case 'heartbeat':
            break;

          case 'sync_progress': {
            const { globalScheduleId, status, message, jobId, result, error } = data;
            if (!globalScheduleId) break;

            if (status === 'in_progress') {
              setSyncingSchedules(prev => new Set(prev).add(globalScheduleId));
              if (message === 'Starting global schedule update...' && jobId) {
                callbacksRef.current.onSyncStart?.(globalScheduleId, jobId);
              } else {
                callbacksRef.current.onSyncProgress?.(globalScheduleId, message || '');
              }
            } else if (status === 'completed') {
              setSyncingSchedules(prev => {
                const next = new Set(prev);
                next.delete(globalScheduleId);
                return next;
              });
              callbacksRef.current.onSyncComplete?.(globalScheduleId, result);
            } else if (status === 'error') {
              setSyncingSchedules(prev => {
                const next = new Set(prev);
                next.delete(globalScheduleId);
                return next;
              });
              callbacksRef.current.onSyncError?.(globalScheduleId, error || 'Update failed');
            }
            break;
          }

          case 'discovery_progress': {
            const { leagueId, phase, percent, message, detail, divisionsFound } = data;
            if (!leagueId) break;
            setDiscoveryProgress(prev => {
              const next = new Map(prev);
              next.set(leagueId, { leagueId, phase, percent, message, detail, divisionsFound });
              return next;
            });
            break;
          }

          case 'discovery_done': {
            const { leagueId } = data;
            if (!leagueId) break;
            setDiscoveryProgress(prev => {
              const next = new Map(prev);
              next.delete(leagueId);
              return next;
            });
            callbacksRef.current.onDiscoveryDone?.(leagueId);
            break;
          }

          case 'discovery_error': {
            const { leagueId, error } = data;
            if (!leagueId) break;
            setDiscoveryProgress(prev => {
              const next = new Map(prev);
              next.delete(leagueId);
              return next;
            });
            callbacksRef.current.onDiscoveryError?.(leagueId, error || 'Discovery failed');
            break;
          }

          case 'cache_stats': {
            const { overall, leagues } = data;
            if (overall && leagues) {
              setCacheStats({ overall, leagues });
            }
            break;
          }
        }
      } catch (error) {
        console.error('[CatalogStream] Failed to parse event:', error);
      }
    };

    es.onerror = () => {
      setIsConnected(false);

      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connect();
        }, delay);
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    syncingSchedules,
    discoveryProgress,
    cacheStats,
    disconnect,
    reconnect: connect,
  };
};
